import React, { useState, useEffect, createContext, useContext } from 'react';

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// Simple router
function useRouter() {
  const [path, setPath] = useState(window.location.pathname);
  const [searchParams, setSearchParams] = useState(new URLSearchParams(window.location.search));

  useEffect(() => {
    const handlePopState = () => {
      setPath(window.location.pathname);
      setSearchParams(new URLSearchParams(window.location.search));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (to) => {
    window.history.pushState({}, '', to);
    setPath(to.split('?')[0]);
    setSearchParams(new URLSearchParams(to.includes('?') ? to.split('?')[1] : ''));
  };

  return { path, searchParams, navigate };
}

// API helper
async function api(endpoint, options = {}) {
  const response = await fetch(`/api${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || 'Something went wrong');
  }
  
  return data;
}

// Components
import Layout from './components/Layout';
import Welcome from './pages/Welcome';
import LeadForm from './pages/LeadForm';
import ThankYou from './pages/ThankYou';
import PromoThankYou from './pages/PromoThankYou';
import PublicTraining from './pages/PublicTraining';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Purchase from './pages/Purchase';
import PurchaseLegacy from './pages/PurchaseLegacy';
import Settings from './pages/Settings';
import MyLeads from './pages/MyLeads';
import AdminDashboard from './pages/AdminDashboard';
import AdminAdvisors from './pages/AdminAdvisors';
import AdminLeads from './pages/AdminLeads';
import Training from './pages/Training';

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { path, searchParams, navigate } = useRouter();

  useEffect(() => {
    // Check if user is logged in
    api('/auth/me')
      .then(data => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await api('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const data = await api('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    setUser(data.user);
    // Navigate after successful registration
    setTimeout(() => {
      navigate('/dashboard');
    }, 100);
    return data;
  };

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    setUser(null);
    navigate('/');
  };

  const authValue = {
    user,
    loading,
    login,
    register,
    logout,
    api,
    navigate,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-navy-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  // Route rendering
  const renderPage = () => {
    // Public routes
    if (path === '/') {
      return <Welcome />;
    }
    if (path === '/lead-form') {
      return <LeadForm />;
    }
    if (path === '/thank-you') {
      return <ThankYou />;
    }
    if (path === '/promo-thank-you') {
      return <PromoThankYou />;
    }
    if (path === '/lead-training') {
      return <PublicTraining />;
    }
    if (path === '/login') {
      if (user) {
        setTimeout(() => navigate(user.isAdmin ? '/admin' : '/dashboard'), 0);
        return null;
      }
      return <Login />;
    }
    if (path === '/forgot-password') {
      return <ForgotPassword />;
    }
    if (path === '/reset-password') {
      const token = searchParams.get('token');
      return <ResetPassword token={token} />;
    }
    if (path === '/register') {
      if (user) {
        setTimeout(() => navigate('/dashboard'), 0);
        return null;
      }
      return <Register />;
    }

    // Protected routes
    if (!user) {
      setTimeout(() => navigate('/login'), 0);
      return null;
    }

    // Advisor routes
    if (path === '/dashboard') {
      return <Dashboard />;
    }
    if (path === '/purchase') {
      return <Purchase searchParams={searchParams} />;
    }
    if (path === '/purchase-legacy') {
      return <PurchaseLegacy searchParams={searchParams} />;
    }
    if (path === '/settings') {
      return <Settings />;
    }
    if (path === '/my-leads') {
      return <MyLeads />;
    }
    if (path === '/training') {
      return <Training />;
    }

    // Admin routes
    if (user.isAdmin) {
      if (path === '/admin' || path === '/admin/dashboard') {
        return <AdminDashboard />;
      }
      if (path === '/admin/advisors') {
        return <AdminAdvisors />;
      }
      if (path === '/admin/leads') {
        return <AdminLeads />;
      }
    }

    // 404
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-display text-navy-900 mb-4">Page Not Found</h1>
          <p className="text-navy-600 mb-6 font-body">The page you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  };

  return (
    <AuthContext.Provider value={authValue}>
      <Layout>
        {renderPage()}
      </Layout>
    </AuthContext.Provider>
  );
}
